import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import classnames from 'classnames';

import ActionButton from 'components/buttons/action-button/ActionButton';
import StatusLabel from 'components/status-label/StatusLabel';
import Banner from 'components/banners/banner/Banner';
import AttachmentsWithViewDelete from 'components/attachments-with-view-delete/AttachmentsWithViewDelete';

import { downloadProofDocuments } from 'modules/organizations/reducers/organizationSettings';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { SUPPORTED_MIMETYPES } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';

import './influencer-settings-document-upload.scss';

applyTranslations(require('./influencer-settings-document-upload.i18n.json'));

import { ProofDocumentFile, UploadFileResponse } from 'types';

type OwnProps = {
  title: string,
  info: string,
  document: UploadFileResponse | null,
  uploadButtonLabel: string,
  canEdit?: boolean,
  isFromSettings?: boolean,
  onDelete: (id: string | number) => void,
  onPreUpload: (data: ProofDocumentFile) => void
}

type DispatchProps = {
  dispatchDownloadDocument: (orgId: number, assetId: number) => void,
  dispatchShowConfirmationModal: (params: {
    title: string,
    message: string,
    cancelButtonLabel: string,
    onCancel: () => void,
    onClose: () => void
  }) => void,
  dispatchHideConfirmationModal: () => void
}

type StateProps = {
  organizationId: number
}

type Props = OwnProps & DispatchProps & StateProps;

const InfluencerSettingsDocumentUpload = ({
  title,
  info,
  uploadButtonLabel,
  document,
  canEdit,
  organizationId,
  isFromSettings,
  onPreUpload,
  onDelete,
  dispatchDownloadDocument,
  dispatchShowConfirmationModal,
  dispatchHideConfirmationModal
}: Props) => {
  let fileInput: HTMLInputElement | undefined | null;

  const handleShowFileDialog = () => {
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const { files } = target;

    if (files) {
      onPreUpload({
        data: files[0],
        signature: ''
      });

      addUserAction({
        eventCategory: 'business-bank-details',
        eventAction: 'business-bank-details-uploaded-document'
      });

      dispatchHideConfirmationModal();
    }

    target.value = '';
  };

  const handleAttachFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatchShowConfirmationModal({
      title: __t('influencer_settings_document_upload.confirm_modal.title'),
      message: __t('influencer_settings_document_upload.confirm_modal.message'),
      cancelButtonLabel: __t('influencer_settings_document_upload.confirm_modal.confirm_btn'),
      onCancel: () => {
        handleUpload(event);
      },
      onClose: () => {
        handleUpload(event);
      }
    });
  };

  if (!canEdit) {
    const classes = classnames('influencer-settings-document-upload__filename', {
      'influencer-settings-document-upload__filename--light': isFromSettings
    });

    return (
      document?.data?.name ? (
        <div
          className={classes}
          onClick={() => {
            dispatchDownloadDocument(organizationId, Number(document.data.id));
          }}
        >
          {document.data.name}
        </div>
      ) : <div>—</div>
    );
  }

  return (
    <div className="influencer-settings-document-upload__upload-container">
      <Banner
        borderRadius={5}
        withBorder
        background="on-white"
      >
        <div className="influencer-settings-document-upload__header">
          <div className="influencer-settings-document-upload__upload-title">
            {title}
          </div>
          {
          // We want to show this tag for approx 30 days
          moment().diff('05/01/2023', 'days') < 30 && (
            <StatusLabel
              label="NEW"
              type="warning"
              layout="marker"
              className="influencer-settings-document-upload__upload-marker"
            />
          )
        }
        </div>
        <div className="influencer-settings-document-upload__upload-info">
          {info}
        </div>
        <input
          ref={(ref) => { fileInput = ref; }}
          className="influencer-settings-document-upload__file"
          onClick={() => {
            addUserAction({
              eventCategory: 'business-bank-details',
              eventAction: 'business_bank_details_upload_initiated'
            });
          }}
          onChange={handleAttachFile}
          type="file"
          accept={SUPPORTED_MIMETYPES.join(',')}
        />
        {
        document ? (
          <AttachmentsWithViewDelete
            attachments={[{
              ...document.data,
              fileName: document.data.name
            }]}
            uniqueId="influencer-settings-document-upload"
            onDelete={onDelete}
            onView={() => {
              dispatchDownloadDocument(organizationId, Number(document.data.id));
            }}
            hideHeader
          />
        ) : (
          <ActionButton
            color="secondary"
            onClick={handleShowFileDialog}
          >
            {uploadButtonLabel}
          </ActionButton>
        )
      }
      </Banner>
    </div>

  );
};

const mapStateToProps = ({ organizations }): StateProps => ({
  organizationId: organizations.currentOrganizationId
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadDocument: (orgId, assetId) => dispatch(downloadProofDocuments(orgId, assetId)),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerSettingsDocumentUpload);
